import { parseValue } from '@root/theme/themeUtils';
import { tokens } from '@root/theme/tokens';
import React from 'react';
import { View, ViewProps, ViewStyle } from 'react-native';

// VStack component that mimics NativeBase VStack behavior
// Arranges children vertically with optional spacing
interface VStackProps extends ViewProps {
  // Layout props
  flex?: number;
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';

  // Spacing between children
  space?: number | string;

  // Spacing props
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string;
  paddingY?: number | string;
  marginX?: number | string;
  marginY?: number | string;

  // Background
  bg?: string;
  backgroundColor?: string;

  // Border
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | number;
  borderRadius?: number;
  borderWidth?: number | string;
  borderColor?: string;

  // Dimensions
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;

  // NativeBase shortcuts
  w?: number | string;
  h?: number | string;
  px?: number | string;
  py?: number | string;
  mx?: number | string;
  my?: number | string;
  mt?: number | string;
  mb?: number | string;
  ml?: number | string;
  mr?: number | string;
  pt?: number | string;
  pb?: number | string;
  pl?: number | string;
  pr?: number | string;

  // Overflow
  overflow?: 'visible' | 'hidden' | 'scroll';

  // NativeBase theme variants
  _dark?: any;
  _light?: any;
  _hover?: any;
  _focus?: any;
  _pressed?: any;
  _disabled?: any;
}

const VStack: React.FC<VStackProps> = ({
  flex,
  justifyContent,
  alignItems,
  alignSelf,
  space,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingX,
  paddingY,
  marginX,
  marginY,
  bg,
  backgroundColor,
  rounded,
  borderRadius,
  borderWidth,
  borderColor,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  // NativeBase shortcuts
  w,
  h,
  px,
  py,
  mx,
  my,
  mt,
  mb,
  ml,
  mr,
  pt,
  pb,
  pl,
  pr,
  overflow,
  style,
  children,
  // Ignore theme variants
  _dark,
  _light,
  _hover,
  _focus,
  _pressed,
  _disabled,
  ...rest
}) => {
  // Use parseValue directly from themeUtils

  // Handle rounded prop
  const getBorderRadius = (): number | undefined => {
    if (borderRadius !== undefined) {
      return parseValue(borderRadius);
    }

    if (rounded !== undefined) {
      if (typeof rounded === 'number') {
        return rounded;
      }

      switch (rounded) {
        case 'sm':
          return tokens.borderRadius.sm;
        case 'md':
          return tokens.borderRadius.md;
        case 'lg':
          return tokens.borderRadius.lg;
        case 'xl':
          return tokens.borderRadius.xl;
        case 'full':
          return tokens.borderRadius.full;
        default:
          return undefined;
      }
    }

    return undefined;
  };

  const vstackStyle: ViewStyle = {
    // VStack specific layout
    flexDirection: 'column',
    justifyContent: justifyContent || 'flex-start',
    alignItems: alignItems || 'stretch',

    // Layout
    flex,
    alignSelf,

    // Spacing - use shortcuts if provided, otherwise use full names
    marginTop: parseValue(mt || marginTop),
    marginBottom: parseValue(mb || marginBottom),
    marginLeft: parseValue(ml || marginLeft),
    marginRight: parseValue(mr || marginRight),
    marginHorizontal: parseValue(mx || marginX),
    marginVertical: parseValue(my || marginY),
    paddingTop: parseValue(pt || paddingTop),
    paddingBottom: parseValue(pb || paddingBottom),
    paddingLeft: parseValue(pl || paddingLeft),
    paddingRight: parseValue(pr || paddingRight),
    paddingHorizontal: parseValue(px || paddingX),
    paddingVertical: parseValue(py || paddingY),

    // Background
    backgroundColor: bg || backgroundColor,

    // Border
    borderRadius: getBorderRadius(),
    borderWidth: parseValue(borderWidth),
    borderColor,

    // Dimensions - use shortcuts if provided, otherwise use full names
    width: parseValue(w || width),
    height: parseValue(h || height),
    minWidth: parseValue(minWidth),
    minHeight: parseValue(minHeight),
    maxWidth: parseValue(maxWidth),
    maxHeight: parseValue(maxHeight),

    // Overflow
    overflow,
  };

  // Extract position-related props from rest and add to style
  const { position, top, right, bottom, left, zIndex, ...remainingRest } = rest as any;
  if (position || top || right || bottom || left || zIndex) {
    Object.assign(vstackStyle, {
      position,
      top,
      right,
      bottom,
      left,
      zIndex,
    });
  }

  // Handle spacing between children
  const childrenArray = React.Children.toArray(children);
  const spacedChildren =
    space && childrenArray.length > 1
      ? childrenArray.map((child, index) => {
          if (index === 0) return child;
          return (
            <View key={index} style={{ marginTop: parseValue(space) }}>
              {child}
            </View>
          );
        })
      : childrenArray;

  return (
    <View style={[vstackStyle, style]} {...remainingRest}>
      {spacedChildren}
    </View>
  );
};

export default VStack;
